/* Copyright (c) 2020-present CloudIO Inc */

import { DBRow, IoUserPagesV } from '@cloudio-saas/datasource-types';
import {
  atom,
  emptyFunction,
  useEndTransition,
  useEvent,
  useStartTransition,
  useStateXActionCaller,
  useStateXAddToUndo,
  useStateXAsyncBatchUpdater,
  useStateXAsyncForceUpdater,
  useStateXBatchUpdater,
  useStateXForceUpdater,
  useStateXGetter,
  useStateXIsMounted,
  useStateXPostUpdateCallbackRegister,
  useStateXQuietCallback,
  useStateXQuietSetter,
  useStateXRefSetter,
  useStateXRemover,
  useStateXScheduler,
  useStateXSetter,
} from '@cloudio-saas/statex';
import { VirtualElementType } from 'components/BingMap/BingMapMain';
import IOPlatform from 'platform/Platform';
import { usePlatform } from 'platform/PlatformHooks';
import { registerPlatformWithServiceWorker } from 'platform/UpdateNotifier';
import React, {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTheme } from 'theme/Theme';
import type { Platform } from '../componentregistry/platform/Platform';
import ErrorBoundary from './ErrorBoundary';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function notInAContext(): any {
  throw Error(
    'This component must be used inside a <PlatformProvider> component.',
  );
}

export const EMPTY_PAGES: DBRow<IoUserPagesV>[] = [];

export const dummyPlatform = new IOPlatform(
  'notInAContextAppUid',
  'notInAContextRoleUid',
  'notInAContextPageId',
  notInAContext,
  notInAContext,
  notInAContext,
  notInAContext,
  notInAContext,
  notInAContext,
  notInAContext,
  notInAContext,
  notInAContext,
  notInAContext,
  notInAContext,
  notInAContext,
  notInAContext,
  notInAContext,
  EMPTY_PAGES,
);
const PlatformContext = createContext<Platform>(dummyPlatform);

export const themeAtom = atom({
  path: ['transient', '$theme'],
  defaultValue: {},
});

function ThemeUpdater() {
  const platform = usePlatform();
  const theme = useTheme();
  useEffect(() => {
    platform.setter(themeAtom, theme);
  }, [platform, theme]);
  platform.theme = theme;
  return null;
}

const PlatformProvider = memo(
  ({
    appUid,
    roleUid,
    children,
    pageId,
    pages,
  }: {
    appUid: string;
    roleUid: string;
    children: React.ReactNode;
    pageId: string;
    pages: DBRow<IoUserPagesV>[];
  }) => {
    const call = useStateXActionCaller();
    const get = useStateXGetter();
    const set = useStateXSetter();
    const remove = useStateXRemover();
    const addToUndo = useStateXAddToUndo('io');
    const setQuietly = useStateXQuietSetter();
    const postUpdateCallbackRegister = useStateXPostUpdateCallbackRegister();
    const forceUpdater = useStateXForceUpdater();
    const stateXScheduler = useStateXScheduler();
    const asyncBatchUpdater = useStateXAsyncBatchUpdater<any>();
    const asyncForceUpdater = useStateXAsyncForceUpdater<any>();
    const startTransition = useStartTransition();
    const endTransition = useEndTransition();
    const setRef = useStateXRefSetter();
    const quietCallback = useStateXQuietCallback();
    const isStateXMounted = useStateXIsMounted();
    const batchUpdater = useStateXBatchUpdater();

    const parentPlatform = useContext(PlatformContext);
    const currentPlatform = useRef<Platform | null>(null);
    const platform = useMemo(() => {
      if (parentPlatform.originalPageId === pageId) {
        return parentPlatform;
      }
      if (
        currentPlatform.current &&
        currentPlatform.current.pageId === pageId &&
        currentPlatform.current.appUid === appUid
      ) {
        currentPlatform.current.roleUid = roleUid;
        currentPlatform.current.setPages(pages);
        return currentPlatform.current;
      }
      const p = new IOPlatform(
        appUid,
        roleUid,
        pageId,
        setQuietly,
        set,
        get,
        call,
        remove,
        addToUndo,
        postUpdateCallbackRegister,
        forceUpdater,
        asyncBatchUpdater,
        asyncForceUpdater,
        batchUpdater,
        stateXScheduler,
        quietCallback,
        isStateXMounted,
        pages,
      );
      currentPlatform.current = p;
      p.registerLoadingIndicator([startTransition, endTransition]);
      p.ws = parentPlatform.ws;
      p.sendRequest = parentPlatform.sendRequest;
      p.callFetch = parentPlatform.callFetch;
      p.atomQueue = parentPlatform.atomQueue;
      p.navLoc = parentPlatform.navLoc;
      return p;
    }, [
      parentPlatform,
      pageId,
      appUid,
      roleUid,
      addToUndo,
      asyncForceUpdater,
      batchUpdater,
      isStateXMounted,
      pages,
    ]);
    if (pageId === '$') {
      registerPlatformWithServiceWorker(platform);
    }

    const ref = useRef(platform);
    ref.current = platform;
    useLayoutEffect(() => {
      setRef(['platform'], ref);
    }, [setRef]);

    return (
      <PlatformContext.Provider value={platform}>
        <ThemeUpdater />
        <ErrorBoundary platform={platform}>{children}</ErrorBoundary>
      </PlatformContext.Provider>
    );
  },
);

PlatformProvider.displayName = 'PlatformProvider';

const MenuContext = createContext<{
  el?: VirtualElementType | null;
  onClose: () => void;
  position?: { left: number; top: number; width: number; height: number };
}>({
  onClose: () => {
    throw Error(
      'This component must be used inside a <MenuContext.Provider> component..',
    );
  },
});

const InfoMenuContext = createContext<{
  el: VirtualElementType | null;
  setEl: (el: VirtualElementType) => void;
  setData: React.Dispatch<React.SetStateAction<unknown | null>>;
  data: unknown;
  onLeave: () => void;
  onEnter: () => void;
}>({
  el: null,
  setEl: emptyFunction,
  data: null,
  setData: emptyFunction,
  onLeave: emptyFunction,
  onEnter: emptyFunction,
});

export function useInfoMenu() {
  return useContext(InfoMenuContext);
}

export function InfoMenu({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const [el, _setEl] = useState<VirtualElementType | null>(null);
  const [data, setData] = useState<unknown>(null);
  const counterRef = useRef(0);

  const setEl = useEvent((ele: VirtualElementType) => {
    counterRef.current++;
    _setEl(ele);
  });

  const onLeave = useEvent(() => {
    const c = counterRef.current;
    setTimeout(() => {
      if (c === counterRef.current) {
        _setEl(null);
      }
    }, 600);
  });

  const onEnter = useCallback(() => {
    counterRef.current++;
  }, []);

  return (
    <InfoMenuContext.Provider
      value={{
        el,
        setEl,
        data,
        setData,
        onLeave,
        onEnter,
      }}
    >
      {children}
    </InfoMenuContext.Provider>
  );
}

export { PlatformProvider, PlatformContext, MenuContext };
